import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from '@/hooks';
import { otherColors } from '@/styles/colors';
import { flex, media } from '@/styles/theme';
import { ChevronLeft, ChevronRight } from '@/assets/icons';
import { NAVBAR_HEIGHT, SIDEBAR_DELAY, Z_INDEX_SIDEBAR, Z_INDEX_SIDEBAR_TOGGLE } from '@/config/variables';
import { PanelLabel } from '@/components/common';
import { Backdrop } from './Backdrop';
import { Mobile, TabletAndDesktop } from '@/breakpoints-config/breakpoints';
import { isSafari, isSmallIOSMobile } from '@/helpers';
import { selectIsMobileScreenSize, selectViewMode } from '@/store/selectors/utilsSelectors';

interface ISidebar {
  title?: string;
  children?: React.ReactNode;
  isCollapsable?: boolean;
  hideCollapsedContent?: boolean;
  width?: string;
}

interface ISidebarItem {
  icon?: string;
  name: string;
  rightText?: string;
  children?: React.ReactNode;
  actionButton?: {
    title: string;
    to: string;
  };
}

interface ISidebarToggle {
  onClick: () => void;
}

const sidebarSelector = createSelector(selectIsMobileScreenSize, selectViewMode, (isMobileScreenSize, viewMode) => ({
  isMobileScreenSize,
  viewMode,
}));

const Sidebar = ({
  title,
  children,
  isCollapsable = true,
  hideCollapsedContent = false,
  width,
}: ISidebar): React.ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const { isMobileScreenSize, viewMode } = useAppSelector(sidebarSelector);

  useEffect(() => {
    if (isMobileScreenSize) {
      setIsCollapsed(prevState => {
        if (!prevState) return true;
        return prevState;
      });
    }
  }, [isMobileScreenSize]);

  /**
   * Handle sidebar for Safari browser
   */
  useEffect(() => {
    if (!isSmallIOSMobile() && !isSafari()) return;
    if (isCollapsed) {
      document.body.style.position = '';
      return;
    }
    document.body.style.position = 'fixed';
    document.body.style.top = '0';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.bottom = '0';
  }, [isCollapsed]);

  return (
    <>
      <SidebarContainer
        width={width}
        className={isCollapsed ? 'collapsed' : ''}
        addSafariPadding={viewMode === 'portrait' && isSmallIOSMobile() && isSafari()}
      >
        {isCollapsable && <SidebarToggle onClick={() => setIsCollapsed(!isCollapsed)} />}
        <ScrollableBody>
          <SidebarContainerTitle>
            <Mobile>
              <div className="sidebar-container-title-mobile">{title}</div>
            </Mobile>
            <TabletAndDesktop>{title ? <PanelLabel>{title}</PanelLabel> : <></>}</TabletAndDesktop>
          </SidebarContainerTitle>
          <SidebarChildren hideCollapsedContent={hideCollapsedContent}>{children}</SidebarChildren>
        </ScrollableBody>
      </SidebarContainer>
      <Backdrop show={isMobileScreenSize && !isCollapsed} />
    </>
  );
};

const SidebarItem = ({ icon, name, rightText, children, actionButton }: ISidebarItem): React.ReactElement => {
  return (
    <SidebarItemContainer>
      <SidebarItemTitle>
        {icon && <SidebarIcon icon={icon} />}
        <MainTitleText>{name}</MainTitleText>
        {rightText && <RightTitleText>{rightText}</RightTitleText>}
        {actionButton && (
          <ActionButton to={actionButton.to}>
            <span>{actionButton.title}</span>
          </ActionButton>
        )}
      </SidebarItemTitle>
      <div>{children}</div>
    </SidebarItemContainer>
  );
};

const SidebarToggle = ({ onClick }: ISidebarToggle): React.ReactElement => (
  <>
    <ToggleButton onClick={onClick} />
    <ToggleButtonBackground />
  </>
);

Sidebar.Item = SidebarItem;

export { Sidebar };

const ScrollableBody = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
`;

const SidebarContainer = styled.div<{ addSafariPadding?: boolean; width?: string }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  position: fixed;
  width: ${props => props.width ?? '384px'};
  max-width: calc(100vw - 17px);
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
  z-index: ${Z_INDEX_SIDEBAR};
  ${({ addSafariPadding }) => addSafariPadding && 'padding-bottom: 15%'};

  background-color: #fff;
  border-right: 1px solid #dee2e6;

  transition: width ${SIDEBAR_DELAY}ms ease-in-out, min-width ${SIDEBAR_DELAY}ms ease-in-out;

  &.collapsed {
    width: 0;
    min-width: 0;

    ${media.tablet} {
      width: 50px;
      min-width: 50px;
    }

    ${ScrollableBody} {
      scrollbar-width: none;

      ::-webkit-scrollbar {
        display: none;
      }
    }
  }

  ${media.tablet} {
    position: relative;
    min-width: ${props => props.width ?? '384px'};
  }
`;

const SidebarContainerTitle = styled.div`
  color: ${otherColors.sidebar.text};
  margin-left: 22px;
  margin-right: 22px;
  margin-top: 22px;
  transition: opacity ${SIDEBAR_DELAY}ms ease-in-out;

  .collapsed & {
    opacity: 0;
  }

  .panel-label {
    padding-bottom: 0.5rem !important;

    h1 {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }

  .sidebar-container-title-mobile {
    font-size: 24px;
    font-weight: 300;
    color: ${otherColors.lxc.darkBlue};
  }
`;

const SidebarChildren = styled.div<{ hideCollapsedContent: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 0 20px 10px 20px;
  transition: margin ${SIDEBAR_DELAY}ms ease-in-out, opacity ${SIDEBAR_DELAY}ms ease-in-out;

  > :not(:last-child) {
    border-bottom: 0 solid #fff;
    transition: border ${SIDEBAR_DELAY}ms ease-in-out, box-shadow ${SIDEBAR_DELAY}ms ease-in-out;
  }

  ${media.phone} {
    margin: 10px 20px;
  }

  .collapsed & {
    opacity: 0;
    margin: 0;

    ${media.tablet} {
      opacity: ${({ hideCollapsedContent }) => (hideCollapsedContent ? 0 : 1)};
      margin-left: 6px;
      margin-right: 6px;

      & > :not(:last-child) {
        border-bottom: 1px solid #dee2e6;
      }
    }
  }
`;

const SidebarItemContainer = styled.div`
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  .collapsed & {
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.075);
  }
`;

const SidebarItemTitle = styled.div.attrs({
  className: `${flex.rowCenter}`,
})`
  color: ${otherColors.sidebar.text};
  background-color: #e6f3fc;
  font-weight: bold;
  line-height: 24px;
  text-transform: uppercase;

  padding-left: 14px;
  padding-right: 14px;
  height: 56px;

  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  transition: opacity ${SIDEBAR_DELAY}ms ease-in-out;

  div {
    &:first-child + div {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;

      & + div {
        font-weight: normal;
      }
    }
  }

  .collapsed & {
    height: 0;
    opacity: 0;
    transition: opacity ${SIDEBAR_DELAY}ms ease-in-out, height ${SIDEBAR_DELAY}ms ease-in-out ${SIDEBAR_DELAY / 2}ms;
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  position: absolute;
  right: -17px;
  top: 12px;
  z-index: ${Z_INDEX_SIDEBAR_TOGGLE};

  width: 34px;
  height: 34px;
  padding: 0;

  background-color: ${otherColors.lxc.blue};
  border-radius: 100%;
  border: 1px solid ${otherColors.lxc.blue};

  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: background-color ${SIDEBAR_DELAY}ms ease-in-out, transform ${SIDEBAR_DELAY}ms, right ${SIDEBAR_DELAY}ms,
    box-shadow ${SIDEBAR_DELAY}ms;

  &::before {
    content: '';
    display: block;
    height: 14px;
    width: 14px;
    background-color: #fff;
    mask-image: url(${ChevronLeft});
    mask-repeat: no-repeat;
    mask-position: center center;
    mask-size: contain;
  }

  .collapsed & {
    background-position: right 9px center;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.1);
    transform: rotate3d(0, 0, 1, 180deg);
    right: -39px;

    ${media.tablet} {
      right: -17px;
    }
  }

  &:active {
    transform: translate3d(2px, 2px, 0);
    transition: none;

    .collapsed & {
      transform: rotate3d(0, 0, 1, 180deg) translate3d(-2px, -2px, 0);
    }
  }

  ${media.tablet} {
    top: 20px;
  }
`;

const ToggleButtonBackground = styled.div`
  width: calc(34px / 2 + 5px);
  height: 34px;
  position: absolute;
  z-index: 1;
  top: 12px;
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  opacity: 0;
  background-color: #fff;

  transition: opacity ${SIDEBAR_DELAY}ms ease-in-out;

  .collapsed & {
    opacity: 1;

    ${media.tablet} {
      opacity: 0;
    }
  }

  ${media.tablet} {
    top: 20px;
  }
`;

const SidebarIcon = styled.div<{ icon: string }>`
  width: 24px;
  height: 24px;
  background-image: url(${({ icon }) => icon});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left;
`;

const MainTitleText = styled.div`
  margin-left: 8px;
`;

const RightTitleText = styled.div`
  margin-left: auto;
`;

const ActionButton = styled(Link)`
  border-radius: 99px;
  border: 1px solid #0087dd;
  background-color: #fff;
  text-transform: none;
  margin-left: auto;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  padding: 2px 12px;
  height: 30px;

  & > span {
    display: flex;
    align-items: center;

    &::after {
      content: '';
      display: block;
      padding-left: 0.75rem;
      padding-right: 0.5rem;
      mask-image: url(${ChevronRight});
      mask-repeat: no-repeat;
      mask-position: 10px center;
      mask-size: 35%;
      height: 14px;
      width: 14px;
      background-color: #0087dd;
    }
  }

  &,
  &:hover {
    color: #0087dd;
    text-decoration: none;
  }
`;
